import React, { useState, useEffect } from 'react';
import {
  FiDollarSign,
  FiCreditCard,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiLock,
  FiRefreshCw,
  FiUploadCloud,
  FiShield,
  FiArrowUpRight,
  FiUserCheck,
  FiChevronRight
} from 'react-icons/fi';
import PayoutService from '../services/PayoutService';

const Payouts = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalSales: 0,
    platformCommission: 0,
    pendingEarnings: 0,
    availableForSettlement: 0,
    totalSettled: 0,
  });

  const [verificationData, setVerificationData] = useState(null);
  const [paymentAccount, setPaymentAccount] = useState(null);
  const [progress, setProgress] = useState({
    step: 1,
    profileSubmitted: false,
    underReview: false,
    approved: false,
    paymentConnected: false,
    settlementActive: false,
  });

  const [ledger, setLedger] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [formMsg, setFormMsg] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  // Verification Form State
  const [form, setForm] = useState({
    applicantType: 'Organization',
    legalName: '',
    organizationName: '',
    mobileNumber: '',
    email: '',
    pan: '',
    gstin: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankName: '',
    accountType: 'CURRENT',
    panDocUrl: '',
    identityDocUrl: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Summary
      const sumRes = await PayoutService.getEarningsSummary().catch(() => null);
      if (sumRes && sumRes.summary) {
        setSummary(sumRes.summary);
      }

      // 2. Fetch Verification & Progress
      const verRes = await PayoutService.getVerificationStatus().catch(() => null);
      if (verRes) {
        if (verRes.verification) {
          setVerificationData(verRes.verification);
          const v = verRes.verification;
          setForm({
            applicantType: v.applicantType || 'Organization',
            legalName: v.legalName || '',
            organizationName: v.organizationName || '',
            mobileNumber: v.mobileNumber || '',
            email: v.email || '',
            pan: v.pan || '',
            gstin: v.gstin || '',
            address: v.address || '',
            city: v.city || '',
            state: v.state || '',
            pinCode: v.pinCode || '',
            accountHolderName: v.bankDetails?.accountHolderName || '',
            accountNumber: '',
            confirmAccountNumber: '',
            ifscCode: v.bankDetails?.ifscCode || '',
            bankName: v.bankDetails?.bankName || '',
            accountType: v.bankDetails?.accountType || 'CURRENT',
            panDocUrl: v.verificationDocuments?.[0]?.url || '',
            identityDocUrl: v.verificationDocuments?.[1]?.url || '',
          });
        }
        if (verRes.paymentAccount) setPaymentAccount(verRes.paymentAccount);
        if (verRes.progress) setProgress(verRes.progress);
      }

      // 3. Fetch Ledger & Settlements
      const ledRes = await PayoutService.getLedger({ limit: 15 }).catch(() => null);
      if (ledRes && ledRes.transactions) setLedger(ledRes.transactions);

      const setRes = await PayoutService.getSettlements({ limit: 15 }).catch(() => null);
      if (setRes && setRes.settlements) setSettlements(setRes.settlements);

    } catch (err) {
      console.error('Error loading payout data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormMsg({ type: '', text: '' });

    if (form.accountNumber && form.accountNumber !== form.confirmAccountNumber) {
      setFormMsg({ type: 'error', text: 'Bank Account Numbers do not match! Please check again.' });
      return;
    }

    setSubmitting(true);
    try {
      const docs = [];
      if (form.panDocUrl) docs.push({ docType: 'PAN_CARD', url: form.panDocUrl });
      if (form.identityDocUrl) docs.push({ docType: 'IDENTITY_PROOF', url: form.identityDocUrl });

      const payload = {
        ...form,
        verificationDocuments: docs,
      };

      const res = await PayoutService.submitVerification(payload);
      if (res && res.success) {
        setFormMsg({ type: 'success', text: 'Verification application submitted successfully! Admin review in progress.' });
        loadData();
      }
    } catch (err) {
      setFormMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to submit verification.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 text-left max-w-6xl mx-auto pb-20">
      {/* Top Bar Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 rounded-xl bg-purple-50 text-purple-700 font-bold">
              <FiDollarSign className="w-5 h-5" />
            </span>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Earnings & Payouts</h1>
          </div>
          <p className="text-xs text-slate-500 font-medium">Manage ticket sales, commission ledger, settlements & Razorpay linked account</p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {summary.activeCommissionRate && (
            <span className="px-3.5 py-2.5 rounded-xl bg-purple-50 border border-purple-200 text-purple-900 text-xs font-black flex items-center gap-1.5 shadow-xs">
              <FiDollarSign className="w-4 h-4 text-purple-700" />
              <span>
                Rate: {summary.activeCommissionRate.value === 0
                  ? '0% Fee'
                  : (summary.activeCommissionRate.type === 'FIXED'
                      ? `₹${summary.activeCommissionRate.value} Fixed / Booking`
                      : `${summary.activeCommissionRate.value}%`)}
              </span>
            </span>
          )}

          <button
            onClick={loadData}
            className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-bold transition flex items-center gap-2 shadow-xs"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {/* 📊 SUMMARY CARDS (5 METRICS) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs space-y-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Sales</span>
          <span className="text-lg font-black text-slate-900">₹{summary.totalSales.toLocaleString('en-IN')}</span>
          <span className="text-[10px] text-slate-500 font-semibold block">Gross Ticket Volume</span>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs space-y-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Platform Fee</span>
          <span className="text-lg font-black text-purple-900">₹{summary.platformCommission.toLocaleString('en-IN')}</span>
          <span className="text-[10px] text-emerald-700 font-bold block">
            {summary.activeCommissionRate
              ? (summary.activeCommissionRate.value === 0
                  ? '0% Fee (100% Sales to You)'
                  : (summary.activeCommissionRate.type === 'FIXED'
                      ? `₹${summary.activeCommissionRate.value} Fixed Fee per Booking`
                      : `${summary.activeCommissionRate.value}% Tapcoe Commission`))
              : 'Tapcoe Commission'}
          </span>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs space-y-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pending</span>
          <span className="text-lg font-black text-amber-700">₹{summary.pendingEarnings.toLocaleString('en-IN')}</span>
          <span className="text-[10px] text-slate-500 font-semibold block">Awaiting Event Completion</span>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs space-y-1 bg-gradient-to-br from-emerald-50/50 to-white border-emerald-200">
          <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider block">Available</span>
          <span className="text-lg font-black text-emerald-700">₹{summary.availableForSettlement.toLocaleString('en-IN')}</span>
          <span className="text-[10px] text-emerald-600 font-bold block">Ready for Payout</span>
        </div>

        <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs space-y-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Settled</span>
          <span className="text-lg font-black text-blue-900">₹{summary.totalSettled.toLocaleString('en-IN')}</span>
          <span className="text-[10px] text-slate-500 font-semibold block">Transferred to Bank</span>
        </div>
      </div>

      {/* 5 TABS NAVIGATION */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        {[
          { key: 'overview', icon: FiFileText, label: 'Overview' },
          { key: 'transactions', icon: FiCreditCard, label: `Transactions (${ledger.length})` },
          { key: 'settlements', icon: FiDollarSign, label: `Settlements (${settlements.length})` },
          { key: 'account', icon: FiShield, label: 'Payment Account' },
          { key: 'verification', icon: FiUserCheck, label: 'Verification & KYC' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 whitespace-nowrap ${
              activeTab === t.key
                ? 'bg-purple-700 text-white shadow-xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            <t.icon className="w-4 h-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* VERIFICATION TRACKER BANNER */}
          <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Verification & Settlement Progress</h3>
                <p className="text-[11px] text-slate-500 font-medium">Complete verification to enable automated Razorpay payouts</p>
              </div>

              <button
                onClick={() => setActiveTab('verification')}
                className="px-3.5 py-1.5 rounded-xl bg-purple-50 text-purple-800 text-xs font-extrabold hover:bg-purple-100 border border-purple-200 transition flex items-center gap-1"
              >
                <span>Manage Verification</span>
                <FiChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* PROGRESS TRACKER STEPS */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 pt-2">
              {[
                { step: 1, label: 'Profile Submitted', done: progress.profileSubmitted },
                { step: 2, label: 'Verification Review', done: progress.underReview },
                { step: 3, label: 'Approved', done: progress.approved },
                { step: 4, label: 'Payment Connected', done: progress.paymentConnected },
                { step: 5, label: 'Settlement Active', done: progress.settlementActive },
              ].map((s) => (
                <div
                  key={s.step}
                  className={`p-3 rounded-2xl border text-center transition ${
                    s.done
                      ? 'bg-emerald-50/70 border-emerald-300 text-emerald-900'
                      : (progress.step === s.step ? 'bg-purple-50 border-purple-300 text-purple-900 font-bold' : 'bg-slate-50 border-slate-200 text-slate-400')
                  }`}
                >
                  <div className="flex items-center justify-center gap-1 text-[11px] font-extrabold mb-1">
                    {s.done ? <FiCheckCircle className="text-emerald-600 w-3.5 h-3.5" /> : <span>Step {s.step}</span>}
                  </div>
                  <span className="text-[11px] font-bold block leading-tight">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RECENT TRANSACTIONS TABLE */}
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Recent Transaction Ledger</h3>
              <button onClick={() => setActiveTab('transactions')} className="text-xs font-bold text-purple-700 hover:underline">
                View All Transactions →
              </button>
            </div>

            {ledger.length === 0 ? (
              <div className="p-12 text-center text-slate-400 font-medium text-xs">
                No financial transactions recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase text-[10px] font-black tracking-wider">
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Event</th>
                      <th className="px-5 py-3">Ticket ID</th>
                      <th className="px-5 py-3">Customer Pay</th>
                      <th className="px-5 py-3">Commission</th>
                      <th className="px-5 py-3">Net Earning</th>
                      <th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledger.slice(0, 5).map((row) => (
                      <tr key={row._id} className="hover:bg-slate-50/70 transition">
                        <td className="px-5 py-3.5 font-semibold text-slate-600">
                          {new Date(row.createdAt).toLocaleDateString('en-IN')}
                        </td>
                        <td className="px-5 py-3.5 font-bold text-slate-900 truncate max-w-[150px]">
                          {row.eventId?.title || 'Event Pass'}
                        </td>
                        <td className="px-5 py-3.5 font-mono text-[11px] text-slate-500">
                          {row.ticketId || row.transactionId}
                        </td>
                        <td className="px-5 py-3.5 font-extrabold text-slate-900">
                          ₹{(row.grossAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-5 py-3.5 font-semibold text-rose-600">
                          -₹{(row.commissionAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-5 py-3.5 font-black text-emerald-700">
                          ₹{(row.netAmount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${
                            row.status === 'AVAILABLE' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : (row.status === 'SETTLED' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-amber-50 text-amber-800 border-amber-200')
                          }`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: TRANSACTIONS LEDGER */}
      {activeTab === 'transactions' && (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs space-y-4">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Financial Transaction Ledger</h3>
              <p className="text-[11px] text-slate-500 font-medium">Complete record of ticket sales, platform commission snapshots & payouts</p>
            </div>
          </div>

          {ledger.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium text-xs">
              No transactions found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase text-[10px] font-black tracking-wider">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Event Name</th>
                    <th className="px-5 py-3">Ticket ID</th>
                    <th className="px-5 py-3">Customer Pay</th>
                    <th className="px-5 py-3">Platform Fee</th>
                    <th className="px-5 py-3">Partner Earning</th>
                    <th className="px-5 py-3">Settlement Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ledger.map((row) => (
                    <tr key={row._id} className="hover:bg-slate-50/70 transition">
                      <td className="px-5 py-3.5 font-semibold text-slate-600">
                        {new Date(row.createdAt).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5 font-bold text-slate-900">
                        {row.eventId?.title || 'Event'}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-[11px] text-slate-500">
                        {row.ticketId || row.transactionId}
                      </td>
                      <td className="px-5 py-3.5 font-extrabold text-slate-900">
                        ₹{(row.grossAmount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-rose-600">
                        -₹{(row.commissionAmount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5 font-black text-emerald-700">
                        ₹{(row.netAmount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${
                          row.status === 'AVAILABLE' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : (row.status === 'SETTLED' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-amber-50 text-amber-800 border-amber-200')
                        }`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: SETTLEMENTS */}
      {activeTab === 'settlements' && (
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Settlement & Bank Payout History</h3>
            <p className="text-[11px] text-slate-500 font-medium">History of automated transfers to your verified bank account</p>
          </div>

          {settlements.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium text-xs">
              No settlements processed yet. Available balance will be settled automatically upon event completion.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase text-[10px] font-black tracking-wider">
                    <th className="px-5 py-3">Settlement Reference</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Bank Account</th>
                    <th className="px-5 py-3">IFSC</th>
                    <th className="px-5 py-3">Initiated Date</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {settlements.map((s) => (
                    <tr key={s._id} className="hover:bg-slate-50/70 transition">
                      <td className="px-5 py-3.5 font-mono text-[11px] font-bold text-slate-900">
                        {s.settlementReference}
                      </td>
                      <td className="px-5 py-3.5 font-black text-emerald-700">
                        ₹{(s.amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-slate-700">
                        {s.maskedAccountNumber || 'XXXX XXXX 1234'}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-slate-600 uppercase">
                        {s.ifscCode || 'IFSC'}
                      </td>
                      <td className="px-5 py-3.5 font-medium text-slate-600">
                        {new Date(s.initiatedAt || s.createdAt).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${
                          s.status === 'PAID' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: PAYMENT ACCOUNT */}
      {activeTab === 'account' && (
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-xs space-y-6">
          <div className="border-b border-slate-100 pb-4">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Razorpay Linked Payment Account Details</h3>
            <p className="text-[11px] text-slate-500 font-medium">Bank account details connected for automated payouts</p>
          </div>

          {paymentAccount ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Masked Bank Account</span>
                <span className="text-base font-black text-slate-900 font-mono">{paymentAccount.maskedAccountNumber || 'XXXX XXXX 1234'}</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">IFSC Code</span>
                <span className="text-base font-black text-purple-900 font-mono uppercase">{paymentAccount.ifscCode || 'IFSC'}</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Razorpay Linked Status</span>
                <span className="px-2.5 py-1 rounded-full text-xs font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200 inline-block">
                  ● {paymentAccount.onboardingStatus || 'ACTIVE'}
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Settlement Status</span>
                <span className="px-2.5 py-1 rounded-full text-xs font-extrabold bg-purple-50 text-purple-800 border border-purple-200 inline-block">
                  ● {paymentAccount.settlementStatus || 'ACTIVE'}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <FiAlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
              <h4 className="text-sm font-bold text-slate-900">Payment Account Not Connected Yet</h4>
              <p className="text-xs text-slate-500 max-w-md mx-auto font-medium">
                Submit your business verification & bank details in the Verification tab to connect your Razorpay Route account.
              </p>
              <button
                onClick={() => setActiveTab('verification')}
                className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition"
              >
                Go to Verification & KYC →
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 5: VERIFICATION FORM */}
      {activeTab === 'verification' && (() => {
        const status = verificationData?.status || 'DRAFT';
        const isPendingReview = ['SUBMITTED', 'UNDER_REVIEW', 'RAZORPAY_ONBOARDING_PENDING'].includes(status);
        const isApproved = ['APPROVED', 'RAZORPAY_ACTIVE'].includes(status);
        const isReadOnly = isPendingReview || isApproved;

        return (
          <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-3xl shadow-xs space-y-6">
            <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">Partner Verification & KYC Application</h3>
                <p className="text-[11px] text-slate-500 font-medium">Submit legal details & bank account for Razorpay Route settlement approval</p>
              </div>
              {verificationData && (
                <span className={`px-3 py-1 rounded-full text-xs font-extrabold border ${
                  isApproved
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : (status === 'REJECTED' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200')
                }`}>
                  Status: {status}
                </span>
              )}
            </div>

            {/* STATUS BANNER */}
            {isPendingReview && (
              <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 space-y-1">
                <div className="flex items-center gap-2">
                  <FiClock className="w-5 h-5 text-amber-600 shrink-0 animate-pulse" />
                  <h4 className="text-xs font-extrabold uppercase tracking-wide">Verification Submitted — Waiting for Admin Review</h4>
                </div>
                <p className="text-xs font-medium text-amber-800 pl-7">
                  Your KYC application and bank details were submitted on {verificationData.submittedAt ? new Date(verificationData.submittedAt).toLocaleDateString('en-IN') : 'recently'}. Tapcoe Admin is reviewing your profile. You cannot re-submit while review is in progress.
                </p>
              </div>
            )}

            {isApproved && (
              <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-1">
                <div className="flex items-center gap-2">
                  <FiCheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                  <h4 className="text-xs font-extrabold uppercase tracking-wide">Verification Approved & Payment Account Active</h4>
                </div>
                <p className="text-xs font-medium text-emerald-800 pl-7">
                  Your business and bank account details have been verified by Admin and linked to Razorpay Route. Automated settlements are active.
                </p>
              </div>
            )}

            {formMsg.text && (
              <div className={`p-4 rounded-2xl text-xs font-bold border ${formMsg.type === 'error' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                {formMsg.text}
              </div>
            )}

            {verificationData?.rejectionReason && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs font-semibold space-y-1">
                <span className="font-bold block uppercase text-[10px]">Action Required — Review Note from Admin:</span>
                <p>{verificationData.rejectionReason}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* PART 1: APPLICANT & BUSINESS DETAILS */}
              <div className="space-y-4">
                <h4 className="text-xs font-black text-purple-900 uppercase tracking-wider border-b border-slate-100 pb-2">
                  1. Business & Contact Information
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Applicant Type</label>
                    <select
                      disabled={isReadOnly}
                      value={form.applicantType}
                      onChange={e => setForm(p => ({ ...p, applicantType: e.target.value }))}
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      <option value="Individual">Individual / Freelancer</option>
                      <option value="Organization">Organization / Registered Company</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Full Legal Name (as per PAN)</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.legalName}
                      onChange={e => setForm(p => ({ ...p, legalName: e.target.value }))}
                      placeholder="e.g. Vikram Sharma"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Organization / Brand Name</label>
                    <input
                      type="text"
                      disabled={isReadOnly}
                      value={form.organizationName}
                      onChange={e => setForm(p => ({ ...p, organizationName: e.target.value }))}
                      placeholder="e.g. Tapcoe Events Pvt Ltd"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Mobile Number</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.mobileNumber}
                      onChange={e => setForm(p => ({ ...p, mobileNumber: e.target.value }))}
                      placeholder="10-digit mobile number"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Email Address</label>
                    <input
                      type="email"
                      required
                      disabled={isReadOnly}
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="partner@example.com"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">PAN Number</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.pan}
                      onChange={e => setForm(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                      placeholder="e.g. ABCDE1234F"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold uppercase disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">GSTIN (Optional)</label>
                    <input
                      type="text"
                      disabled={isReadOnly}
                      value={form.gstin}
                      onChange={e => setForm(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                      placeholder="15-digit GST number"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold uppercase disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Complete Address</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.address}
                      onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                      placeholder="Business address"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">City</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.city}
                      onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                      placeholder="City"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">State</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.state}
                      onChange={e => setForm(p => ({ ...p, state: e.target.value }))}
                      placeholder="State"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Pin Code</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.pinCode}
                      onChange={e => setForm(p => ({ ...p, pinCode: e.target.value }))}
                      placeholder="6-digit pincode"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>
                </div>
              </div>

              {/* PART 2: BANK DETAILS */}
              <div className="space-y-4 pt-2">
                <h4 className="text-xs font-black text-purple-900 uppercase tracking-wider border-b border-slate-100 pb-2">
                  2. Bank Account Details (Masked Security)
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Account Holder Name</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.accountHolderName}
                      onChange={e => setForm(p => ({ ...p, accountHolderName: e.target.value }))}
                      placeholder="Name as registered in bank"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Bank Name</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.bankName}
                      onChange={e => setForm(p => ({ ...p, bankName: e.target.value }))}
                      placeholder="e.g. HDFC Bank / ICICI Bank"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Account Number</label>
                    <input
                      type="password"
                      required={!isReadOnly}
                      disabled={isReadOnly}
                      value={isReadOnly ? '••••••••••••' : form.accountNumber}
                      onChange={e => setForm(p => ({ ...p, accountNumber: e.target.value }))}
                      placeholder="Full account number"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Confirm Account Number</label>
                    <input
                      type="text"
                      required={!isReadOnly}
                      disabled={isReadOnly}
                      value={isReadOnly ? (verificationData?.bankDetails?.maskedAccountNumber || 'XXXX XXXX 1234') : form.confirmAccountNumber}
                      onChange={e => setForm(p => ({ ...p, confirmAccountNumber: e.target.value }))}
                      placeholder="Re-enter account number"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">IFSC Code</label>
                    <input
                      type="text"
                      required
                      disabled={isReadOnly}
                      value={form.ifscCode}
                      onChange={e => setForm(p => ({ ...p, ifscCode: e.target.value.toUpperCase() }))}
                      placeholder="e.g. HDFC0001234"
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold uppercase disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Account Type</label>
                    <select
                      disabled={isReadOnly}
                      value={form.accountType}
                      onChange={e => setForm(p => ({ ...p, accountType: e.target.value }))}
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold disabled:bg-slate-100 disabled:text-slate-500"
                    >
                      <option value="CURRENT">Current Account</option>
                      <option value="SAVINGS">Savings Account</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SUBMIT / STATUS BUTTON */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                {isPendingReview ? (
                  <button
                    type="button"
                    disabled
                    className="px-6 py-3 rounded-2xl bg-amber-100 text-amber-900 border border-amber-300 text-xs font-extrabold flex items-center gap-2 cursor-not-allowed opacity-90"
                  >
                    <FiClock className="w-4 h-4 text-amber-700 animate-pulse" />
                    <span>⏳ Submitted — Waiting for Admin Verification</span>
                  </button>
                ) : isApproved ? (
                  <button
                    type="button"
                    disabled
                    className="px-6 py-3 rounded-2xl bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs font-extrabold flex items-center gap-2 cursor-not-allowed"
                  >
                    <FiCheckCircle className="w-4 h-4 text-emerald-700" />
                    <span>✅ Verification Approved & Account Active</span>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-extrabold transition shadow-sm flex items-center gap-2"
                  >
                    {submitting ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiUploadCloud className="w-4 h-4" />
                    )}
                    <span>{status === 'RESUBMISSION_REQUIRED' ? 'Update & Resubmit Verification Application' : 'Submit Verification Application'}</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        );
      })()}
    </div>
  );
};

export default Payouts;
